import { CssBaseline, useMediaQuery } from '@mui/material';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import React, { ReactNode, createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { theme as baseTheme, theme } from '../theme';
import { notifyBackgroundColorChange } from '../utils/webViewBridge';

type ThemeContextType = {
  setPageBackground: (color: string) => void;
  resetBackground: () => void;
};

const ThemeContext = createContext<ThemeContextType>({
  setPageBackground: () => { },
  resetBackground: () => { },
});

export const useThemeContext = () => useContext(ThemeContext);

// This component will be used inside Router context
export const RouteChangeListener = () => {
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { setPageBackground, resetBackground } = useThemeContext();

  // Memoize the page background colors to prevent recreation on every render
  const pageBgColors = useMemo(() => ({
    [ROUTES.HOME]: theme.palette.common.white,
    [ROUTES.ASSETS]: theme.palette.pink[500],
    [ROUTES.OVERVIEW]: theme.palette.pink[500],
    [ROUTES.WALLETS]: theme.palette.navy[900],
    [ROUTES.HISTORY]: theme.palette.blue[300],
    [ROUTES.PROFILE]: baseTheme.palette.background.default,
    [ROUTES.LOGIN]: baseTheme.palette.background.default,
    [ROUTES.SIGN_UP]: baseTheme.palette.background.default,
    [ROUTES.FORGOT_PASSWORD]: baseTheme.palette.background.default,
    [ROUTES.VERIFY_OTP]: baseTheme.palette.background.default,
    [ROUTES.MINO_TERMS]: baseTheme.palette.background.default,
    [ROUTES.UPGRADE_ACCOUNT]: baseTheme.palette.background.default,
    [ROUTES.EDIT_BASIC_WALLET]: theme.palette.pink[100],
    [ROUTES.BASIC_WALLET_DETAIL]: theme.palette.pink[100],
    [ROUTES.WALLET_INSTALLMENT]: theme.palette.purple[100],
    [ROUTES.EDIT_WALLET]: theme.palette.purple[100],
    [ROUTES.CREDIT_WALLET_DETAIL]: theme.palette.purple[100],
    [ROUTES.CREDIT_STATEMENT_DETAIL]: baseTheme.palette.background.default,
    [ROUTES.CREATE_BASIC_TRANSACTION]: theme.palette.navy[900],
    [ROUTES.EDIT_BASIC_TRANSACTION]: theme.palette.navy[900],
    [ROUTES.EDIT_LOAN_TRANSACTION]: theme.palette.navy[900],
    [ROUTES.BORROWING_TRANSACTION_EDIT]: theme.palette.navy[900],
    [ROUTES.REPAYMENT_TRANSACTION_EDIT]: theme.palette.navy[900],
    [ROUTES.LENDING_TRANSACTION_EDIT]: theme.palette.navy[900],
    [ROUTES.COLLECTING_TRANSACTION_EDIT]: theme.palette.navy[900],
    [ROUTES.CLEAR_DEBT_TRANSACTION_EDIT]: theme.palette.navy[900],
    [ROUTES.DISCOVER]: isDesktop ? baseTheme.palette.background.default : theme.palette.navy[900],
    [ROUTES.LOAN_WALLET_DETAIL]: theme.palette.teal[100],
    [ROUTES.LOAN_WALLET_OFFSET]: theme.palette.teal[100],
    [ROUTES.EDIT_LOAN_WALLET]: theme.palette.teal[100],
    [ROUTES.BUDGET]: theme.palette.cyan[500],
    [ROUTES.SETTINGS]: theme.palette.navy[200],
  }), [isDesktop]);

  // Memoize the function to find matching route color
  const getMatchingRouteColor = useCallback(() => {
    // Try exact match first
    if (pageBgColors[location.pathname as keyof typeof pageBgColors]) {
      return pageBgColors[location.pathname as keyof typeof pageBgColors];
    }

    // Then try pattern matching
    for (const route of Object.keys(pageBgColors)) {
      if (matchPath({ path: route }, location.pathname)) {
        return pageBgColors[route as keyof typeof pageBgColors];
      }
    }

    return baseTheme.palette.background.default;
  }, [location.pathname, pageBgColors]);

  // Reset before navigation - when location changes
  useEffect(() => {
    // This runs before the page actually renders with new content
    setPageBackground(getMatchingRouteColor());

    // Return cleanup function that runs before the next location change
    return () => {
      resetBackground();
    };
  }, [location.key, getMatchingRouteColor, setPageBackground, resetBackground]);

  return null;
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [backgroundColor, setBackgroundColor] = useState(baseTheme.palette.background.default);

  const theme = useMemo(() => {
    return createTheme({
      ...baseTheme,
      palette: {
        ...baseTheme.palette,
        background: {
          ...baseTheme.palette.background,
          default: backgroundColor,
        },
      },
    });
  }, [backgroundColor]);

  const setPageBackground = useCallback((color: string) => {
    setBackgroundColor(color);
    // Notify React Native WebView about the background color change
    notifyBackgroundColorChange(color);
  }, []);

  const resetBackground = useCallback(() => {
    const defaultColor = baseTheme.palette.background.default;
    setBackgroundColor(defaultColor);
    // Notify React Native WebView about the reset to default background color
    notifyBackgroundColorChange(defaultColor);
  }, []);

  const contextValue = useMemo(() => ({
    setPageBackground,
    resetBackground,
  }), [setPageBackground, resetBackground]);

  // Initialize background color only once on mount
  useEffect(() => {
    const defaultColor = baseTheme.palette.background.default;
    setBackgroundColor(defaultColor);
    notifyBackgroundColorChange(defaultColor);
  }, []);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};